import type {
  JoinInfo,
  ReferralsMe,
  ReferralInvitee,
  ReferralStatus,
  ReferralFlag,
  AdminReferralsSummary,
  AdminReferralEvent,
} from "@atlas/shared";
import { apiGet, apiPatch } from "../lib/api-client";

export type {
  JoinInfo,
  ReferralsMe,
  ReferralInvitee,
  ReferralStatus,
  ReferralFlag,
  AdminReferralsSummary,
  AdminReferralEvent,
};

/** localStorage key holding the referrer's username between the join page and signup. */
export const REFERRAL_STORAGE_KEY = "atlas-referral";

export function readStoredReferral(): string | null {
  try {
    return window.localStorage.getItem(REFERRAL_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storeReferral(username: string): void {
  try {
    window.localStorage.setItem(REFERRAL_STORAGE_KEY, username);
  } catch {
    // Private mode or blocked storage: the ?ref= search param still carries it.
  }
}

export function clearStoredReferral(): void {
  try {
    window.localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function fetchJoinInfo(username: string): Promise<JoinInfo> {
  return apiGet(`/join/${encodeURIComponent(username)}`);
}

export function fetchReferralsMe(): Promise<ReferralsMe> {
  return apiGet("/referrals/me");
}

export function fetchAdminReferrals(): Promise<AdminReferralsSummary> {
  return apiGet("/admin/referrals");
}

export function fetchAdminReferralEvents(params: {
  status?: ReferralStatus;
  flag?: ReferralFlag;
} = {}): Promise<{ events: AdminReferralEvent[] }> {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.flag) qs.set("flag", params.flag);
  const suffix = qs.toString();
  return apiGet(`/admin/referrals/events${suffix ? `?${suffix}` : ""}`);
}

export function resolveAdminReferralEvent(
  id: string,
  body: { action: "credit" | "reject"; note?: string },
): Promise<{ event: { id: string; status: ReferralStatus; creditedAt: string | null; note: string | null } }> {
  return apiPatch(`/admin/referrals/events/${id}`, body);
}
